/* eslint-disable react-hooks/rules-of-hooks */
import { useCallback, useEffect, useMemo } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'
import styled from '@emotion/styled'
import { Checkbox, Typography } from '@mui/material'
import { DragIndicator } from '@emotion-icons/material/DragIndicator'
import { Text } from '@emotion-icons/boxicons-regular/Text'
import { Image } from '@emotion-icons/boxicons-solid/Image'
import { Map } from '@emotion-icons/fa-solid/Map'
import { DragEndEvent } from '@dnd-kit/core'
import { arrayMove } from '@dnd-kit/sortable'
import { Colors, Shapes } from 'src/components/theme'
import { Panel } from 'src/components/sidebar/panel'
import { PanelContents, PanelFormControlLabel, PanelProps } from 'src/views/insight-download/modal/content'
import { SortableItem, SortableProvider } from 'src/components/dnd/sortable-list'
import { InsightWidget } from 'src/types/report-insights'

import * as reportInsight from 'src/atoms/report-insight'
import { LockedFeature } from 'src/components/locked-feature'

const WidgetLabelWrapper = styled(Typography)`
  display: flex;
  align-items: center;
  gap: ${Shapes.spacing[1]}px;
  & > span {
    max-width: 165px;
    padding-right: ${Shapes.spacing[1]}px;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2; /* number of lines to show */
    line-clamp: 2;
    -webkit-box-orient: vertical;
  }
`

type WidgetLabelProps = {
  widget: InsightWidget | Partial<InsightWidget>
}

const WidgetLabel = (props: WidgetLabelProps) => {
  const { widget } = props
  return (
    <WidgetLabelWrapper variant="text1">
      {widget.type === 'map' && <Map size={16} />}
      {widget.type === 'text' && <Text size={16} />}
      {widget.type === 'image' && <Image size={16} />}
      <span>{widget.label}</span>
    </WidgetLabelWrapper>
  )
}

const SectionsPanelTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-inline: ${Shapes.spacing[4]}px;
  padding-bottom: ${Shapes.spacing[2]}px;
`

const WidgetItem = styled.div`
  padding-block: ${Shapes.spacing[1] / 2}px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`

export const SectionsPanel = (props: PanelProps) => {
  const { loading } = props
  const [widgets, setWidgets] = useRecoilState(reportInsight.widgets)
  const lockedWidgets = useRecoilValue(reportInsight.lockedWidgets)
  const isSelectAll = useMemo(() => widgets.every((widget) => widget.visible), [widgets])

  const onDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event
    if (!over) {
      return
    }
    if (active.id !== over.id) {
      setWidgets((widgets) => {
        const oldIndex = widgets.findIndex((widget) => widget.id === active.id)
        const newIndex = widgets.findIndex((widget) => widget.id === over.id)

        return arrayMove(widgets, oldIndex, newIndex)
      })
    }
  }, [])

  const toggleWidget = useCallback(
    (widgetId: string) => {
      const toggledWidgetIndex = widgets.findIndex((widget) => widget.id === widgetId)
      if (toggledWidgetIndex === -1) {
        return
      }
      setWidgets((widgets) => {
        const toggledWidget = widgets[toggledWidgetIndex]
        return [
          ...widgets.slice(0, toggledWidgetIndex),
          {
            ...toggledWidget,
            visible: !toggledWidget.visible,
          },
          ...widgets.slice(toggledWidgetIndex + 1),
        ]
      })
    },
    [widgets]
  )

  const onToggleAll = useCallback(() => {
    setWidgets((widgets) =>
      widgets.map((widget) => ({
        ...widget,
        visible: !isSelectAll,
      }))
    )
  }, [isSelectAll])

  const Title = (
    <SectionsPanelTitle>
      <Typography variant="textmd" color={Colors.textBlack}>
        Sections
      </Typography>
    </SectionsPanelTitle>
  )

  useEffect(() => {
    setWidgets(widgets)
  }, [widgets])

  return (
    <Panel title={Title}>
      <PanelContents>
        <PanelFormControlLabel
          label={`${isSelectAll ? 'Deselect All' : 'Select All'}`}
          disabled={loading}
          control={<Checkbox checked={isSelectAll} onChange={onToggleAll} />}
        />
        <SortableProvider items={widgets} onDragEnd={onDragEnd}>
          {widgets.map((widget) => (
            <SortableItem key={widget.id} id={widget.id}>
              <WidgetItem>
                <PanelFormControlLabel
                  disabled={loading}
                  control={<Checkbox checked={widget.visible} onChange={() => toggleWidget(widget.id)} />}
                  label={<WidgetLabel widget={widget} />}
                />
                <DragIndicator size={Shapes.spacing[4]} />
              </WidgetItem>
            </SortableItem>
          ))}
          {lockedWidgets.map((widget) => (
            <WidgetItem>
              <PanelFormControlLabel
                disabled={loading}
                control={<LockedFeature isLocked />}
                label={<WidgetLabel widget={widget} />}
              />
            </WidgetItem>
          ))}
        </SortableProvider>
      </PanelContents>
    </Panel>
  )
}
